import React from 'react';
import { useTranslation } from 'react-i18next';
import type { PlanCardProps } from './types';
import { getCurrency, formatPrice } from './utils';
import './styles/index.less';

const PlanCard: React.FC<PlanCardProps> = ({ plan, isCurrent, onSelect, selected, disabled = false }) => {
  const { t, i18n } = useTranslation();
  const currency = getCurrency(i18n.language);

  return (
    <div 
      className={`plan-card ${selected ? 'selected' : ''} ${isCurrent ? 'current' : ''}`}
      onClick={disabled ? undefined : onSelect}
      style={{ cursor: disabled ? 'default' : 'pointer' }}
    >
      {/* 如果当前计划也是推荐计划，只显示当前标签 */}
      {isCurrent ? (
        <div className="plan-badge current-badge">
          {t('account.current') || 'Current'}
        </div>
      ) : plan.popular && (
        <div className="plan-badge popular-badge">
          {t('account.popular') || 'Popular'}
        </div>
      )}

      <div className="plan-header">
        <h4 className="plan-name">{plan.name}</h4>
        <div className="plan-hours">
          <svg className="w-4 h-4 hours-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <polyline points="12,6 12,12 16,14"/>
          </svg>
          <span>
            {plan.hours === 'Unlimited' 
              ? (t('account.unlimited') || 'Unlimited')
              : `${plan.hours} ${t('account.hours') || 'hours'}`
            }
          </span>
        </div>
        <div className="plan-price">
          {plan.originalPrice !== undefined && (
            <div className="flex items-center justify-center gap-2 mb-0.5">
              <span className="text-sm text-gray-400 line-through">{formatPrice(plan.originalPrice, currency)}</span>
              <span className="text-xs font-semibold text-red-500 bg-red-50 rounded px-1.5 py-0.5 whitespace-nowrap">
                {plan.discountPercent}% OFF
              </span>
            </div>
          )}
          <span className="price-amount">{formatPrice(plan.price, currency)}</span>
          <span className="price-period">
            {plan.firstMonth && plan.originalPrice !== undefined
              ? ` ${t('account.firstMonthThen', { price: formatPrice(plan.originalPrice, currency) })}`
              : `/${t('account.month') || 'month'}`}
          </span>
          {plan.firstMonth && (
            /* 适用条件须紧邻折扣价展示且字号不明显偏小（景表法：条件不能藏） */
            <div className="text-xs text-gray-500 mt-0.5">
              {t('account.firstMonthConditions')}
            </div>
          )}
        </div>
      </div>

      <ul className="plan-features">
        {plan.features.map((feature, index) => (
          <li key={index}>
            <svg className="check-icon w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="20,6 9,17 4,12"/>
            </svg>
            <span>{feature}</span>
          </li>
        ))}
      </ul>

      <button 
        className={`plan-button ${
          isCurrent 
            ? 'current-plan' 
            : selected 
              ? 'selected-plan' 
              : 'default'
        }`}
        disabled={isCurrent}
      >
        {isCurrent 
          ? (t('account.currentPlan') || 'Current Plan')
          : selected 
            ? (t('account.selected') || 'Selected')
            : (t('account.selectPlan') || 'Select Plan')
        }
      </button>
    </div>
  );
};

export default PlanCard;